import { useMutation } from "@tanstack/react-query"
import { RotateCw, ScanText } from "lucide-react"
import { useEffect, useState } from "react"

import { api, ErrorApi } from "@/api/cliente"
import { ProgresoLectura } from "@/componentes/ordenes/ProgresoLectura"
import {
  LEGIBLES,
  type LecturaPersonal,
  type Propuesta,
} from "@/componentes/personas/lectorPersonal"
import type { Persona } from "@/componentes/personas/persona"
import { RevisionLectura } from "@/componentes/personas/RevisionLectura"
import {
  type Adjuntando,
  AdjuntosNuevos,
  archivosPegados,
  sumar,
} from "@/componentes/ui/AdjuntosNuevos"
import { Boton } from "@/componentes/ui/Boton"

/** Lo que el servidor admite por lectura (`lector_facturas.MAXIMO_ARCHIVOS`). */
const MAXIMO = 5

/**
 * Lee con IA la cédula, el RIF y la síntesis curricular de una persona.
 *
 * ⚠ **Todos los archivos en UNA lectura, sin decir cuál es cuál**: el modelo lo
 * identifica y la revisión lo enseña. Preguntarlo sería pedir lo que se ve solo.
 *
 * ⚠ **Se lee SÓLO si se pide, y sale a Google**: el aviso va al lado del botón, no en
 * una nota al pie. Lo pide `persona:leer_ia`; quien no lo tiene ni ve la entrada.
 *
 * ⚠ **El Ctrl+V se escucha en el DOCUMENTO, no en el panel** (como `LectorRetencion`):
 * `AdjuntosNuevos` deja el pegado a quien lo usa, y la zona punteada no toma el foco, así
 * que tras pulsarla el pegado llega al `body` y un `onPaste` del panel no lo vería. Sólo
 * mientras se eligen archivos, y sólo se corta el pegado si trae archivos: pegar texto
 * en un campo sigue siendo pegar texto.
 *
 * Con `personaId` se lee para completar ESA ficha, y el servidor rechaza documentos
 * de otra cédula; sin él, es un alta.
 */
export function LectorDePersonal({
  personaId,
  actual,
  onUsar,
  onCerrar,
  onAbrirFicha,
  iniciales,
}: {
  personaId?: number
  actual: (campo: keyof Persona) => string
  onUsar: (p: Propuesta) => void
  onCerrar: () => void
  onAbrirFicha?: (id: number) => void
  /** (23/10) Archivos que llegan del checklist laboral: se cargan y se leen solos. */
  iniciales?: File[]
}) {
  const [adjuntos, setAdjuntos] = useState<Adjuntando[]>([])
  const [rechazados, setRechazados] = useState<string[]>([])
  const [leerSolo, setLeerSolo] = useState(false)

  const leer = useMutation({
    mutationFn: () => {
      const datos = new FormData()
      for (const a of adjuntos) datos.append("archivos", a.archivo)
      if (personaId !== undefined) datos.append("persona_id", String(personaId))
      return api.post<LecturaPersonal>("/api/v1/personas/lector-ia", datos)
    },
  })

  function anadir(archivos: File[]) {
    // Gemini lee PDF e imágenes; un Word pasaría el portero y lo rechazaría el modelo.
    const legibles = archivos.filter((f) => LEGIBLES.includes(f.type))
    const ilegibles = archivos
      .filter((f) => !LEGIBLES.includes(f.type))
      .map((f) => `${f.name}: sólo se leen PDF y fotos (JPG, PNG, WEBP)`)
    const resultado = sumar(adjuntos, legibles)
    const sobran = resultado.adjuntos.length - MAXIMO
    setAdjuntos(resultado.adjuntos.slice(0, MAXIMO))
    setRechazados([
      ...ilegibles,
      ...resultado.rechazados,
      ...(sobran > 0 ? [`Como mucho ${MAXIMO} archivos por lectura`] : []),
    ])
  }

  // Los del checklist entran una vez al abrir y se leen sin pulsar nada: quien los subió
  // desde «Subir y leer con IA» ya pidió leerlos.
  // biome-ignore lint/correctness/useExhaustiveDependencies: sólo al abrir
  useEffect(() => {
    if (iniciales && iniciales.length > 0) {
      anadir(iniciales)
      setLeerSolo(true)
    }
  }, [])
  useEffect(() => {
    if (leerSolo && adjuntos.length > 0 && !leer.isPending && !leer.data) {
      setLeerSolo(false)
      leer.mutate()
    }
  }, [leerSolo, adjuntos.length, leer])

  const eligiendo = !leer.data && !leer.isPending
  useEffect(() => {
    if (!eligiendo) return
    function alPegar(evento: ClipboardEvent) {
      const archivos = archivosPegados(evento.clipboardData, adjuntos.length)
      if (archivos.length === 0) return
      evento.preventDefault()
      anadir(archivos)
    }
    document.addEventListener("paste", alPegar)
    return () => document.removeEventListener("paste", alPegar)
  })

  if (leer.data) {
    return (
      <RevisionLectura
        lectura={leer.data}
        actual={actual}
        onUsar={onUsar}
        onIgnorar={onCerrar}
        onAbrirFicha={onAbrirFicha}
      />
    )
  }
  if (leer.isPending) return <ProgresoLectura archivos={adjuntos.length} documento="personal" />

  return (
    <div className="space-y-3 text-sm">
      <p style={{ color: "var(--fg-muted)" }}>
        Sube la cédula, el RIF y la síntesis curricular —en PDF o foto, en cualquier orden— y el
        lector propone los datos de la ficha. Nada se guarda hasta que lo revises.
      </p>
      <AdjuntosNuevos
        adjuntos={adjuntos}
        rechazados={rechazados}
        etiqueta="Adjuntar documentos"
        onArchivos={anadir}
        onQuitar={(id) => setAdjuntos((previos) => previos.filter((a) => a.id !== id))}
      />
      {leer.isError && (
        <p role="alert" style={{ color: "var(--destructive)" }}>
          No se pudieron leer:{" "}
          {leer.error instanceof ErrorApi ? leer.error.message : "error inesperado"}. Reintenta o
          teclea los datos a mano.
        </p>
      )}
      <div className="flex flex-wrap items-center justify-end gap-2">
        <span className="text-xs" style={{ color: "var(--fg-muted)" }}>
          Los documentos se envían a Google (Gemini) para leerlos.
        </span>
        <Boton tamano="sm" onClick={onCerrar}>
          Cancelar
        </Boton>
        <Boton
          tamano="sm"
          variante="primario"
          disabled={adjuntos.length === 0}
          onClick={() => leer.mutate()}
        >
          {leer.isError ? <RotateCw size={14} /> : <ScanText size={14} />}
          {leer.isError ? "Reintentar" : "Leer con IA"}
        </Boton>
      </div>
    </div>
  )
}
